import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { useFormatMessage } from 'src/providers/intl'
import history from 'src/history'
import { OVERLAY_HELPERS } from 'src/constants/navigation'
import { EMERGENCY_RESERVE } from 'src/constants/gems'
import {
  ViewCashoutFromWIN,
  ViewReliefFromWIN,
  ViewRefiFromWIN,
  ViewShareFromWIN
} from 'src/constants/trackingEvents'
import { Text, Format } from 'src/Components'
import {
  selectShouldRenderRefiOptions,
  selectShouldRenderMortgageRelief,
  selectShouldRenderEmergencyReserve
} from 'src/store/selectors/display'
import {
  selectRefiShorttermSavings,
  selectRefiShorttermSavingsKey
} from 'src/store/selectors/refinance'
import { selectEmergencyReservePool } from 'src/store/selectors/home/equity'
import OverlayPanel from 'src/Components/shared/Routing/OverlayPanel'
import SharePanel from 'src/Components/homeowners/SharePanel/SharePanel'
import MortgageRelief from 'src/Components/shared/EstimatedValue/MortgageRelief'
import { dispatchEvent } from 'src/helpers/tagManager'

import styles from './ImportantNow.module.scss'
import ImportantNowSpotlight from './ImportantNowSpotlight'

const ImportantNow = ({ locked }) => {
  const intlFormat = useFormatMessage()

  const cashout = useSelector(selectEmergencyReservePool)
  const refiSavings = useSelector(selectRefiShorttermSavings)
  const refiSavingsKey = useSelector(selectRefiShorttermSavingsKey)

  const displayReserve = useSelector(selectShouldRenderEmergencyReserve)
  const displayRelief = useSelector(selectShouldRenderMortgageRelief)
  const displayRefi =
    useSelector(selectShouldRenderRefiOptions) && refiSavings > 0

  return (
    <>
      <div className={styles.container}>
        <Text element='div' className={styles.title}>
          <FormattedMessage
            id='ImportantNow.title'
            defaultMessage="What's important now"
          />
        </Text>
        <ImportantNowSpotlight
          name='Reserve'
          hide={!displayReserve}
          locked={locked}
          onClick={() => {
            if (!locked) {
              dispatchEvent(ViewCashoutFromWIN)
              history.push(OVERLAY_HELPERS.cashout.buildPath(EMERGENCY_RESERVE))
            }
          }}
        >
          <FormattedMessage
            id='ImportantNow.spotlight.cashout'
            defaultMessage='{bold} by cashing out around {cash}'
            values={{
              bold: (
                <Text element='span' weight='semibold'>
                  <FormattedMessage
                    id='ImportantNow.spotlight.cashout.bold'
                    defaultMessage='Set up a safety net fund'
                  />
                </Text>
              ),
              cash: (
                <Text
                  element='span'
                  weight='semibold'
                  className={locked && styles.blur}
                >
                  <Format.Currency value={!locked && cashout} abbreviate />
                </Text>
              )
            }}
          />
        </ImportantNowSpotlight>
        <ImportantNowSpotlight
          name='Refi'
          hide={!displayRefi}
          locked={locked}
          onClick={() => {
            if (!locked) {
              dispatchEvent(ViewRefiFromWIN)
              history.push(OVERLAY_HELPERS.refinance.buildPath(refiSavingsKey))
            }
          }}
        >
          <FormattedMessage
            id='ImportantNow.spotlight.refi'
            defaultMessage='You could refi to {firstBold}{cash}{secondBold}'
            values={{
              firstBold: (
                <Text element='span' weight='semibold'>
                  <FormattedMessage
                    id='ImportantNow.spotlight.refi.firstBold'
                    defaultMessage='drop your payment by '
                  />
                </Text>
              ),
              cash: (
                <Text
                  element='span'
                  weight='semibold'
                  className={locked && styles.blur}
                >
                  <Format.Currency value={!locked && refiSavings} abbreviate />
                </Text>
              ),
              secondBold: (
                <Text element='span' weight='semibold'>
                  <FormattedMessage
                    id='ImportantNow.spotlight.refi.bold'
                    defaultMessage='/month'
                  />
                </Text>
              )
            }}
          />
        </ImportantNowSpotlight>
        <ImportantNowSpotlight
          name='Share'
          locked={locked}
          onClick={() => {
            if (!locked) {
              dispatchEvent(ViewShareFromWIN)
              history.push(OVERLAY_HELPERS.shareHomebot.buildPath())
            }
          }}
        >
          <FormattedMessage
            id='ImportantNow.spotlight.share'
            defaultMessage='{bold} to help friends and family'
            values={{
              bold: (
                <Text element='span' weight='semibold'>
                  <FormattedMessage
                    id='ImportantNow.spotlight.share.bold'
                    defaultMessage='Share Homebot'
                  />
                </Text>
              )
            }}
          />
        </ImportantNowSpotlight>
        <ImportantNowSpotlight
          name='Relief'
          hide={!displayRelief}
          locked={locked}
          onClick={() => {
            if (!locked) {
              dispatchEvent(ViewReliefFromWIN)
              history.push(OVERLAY_HELPERS.mortgageRelief.buildPath())
            }
          }}
        >
          <FormattedMessage
            id='ImportantNow.spotlight.relief'
            defaultMessage='Worried about mortgage payments? {bold} of relief options'
            values={{
              bold: (
                <Text element='span' weight='semibold'>
                  <FormattedMessage
                    id='ImportantNow.spotlight.relief.bold'
                    defaultMessage='Know the pro and cons'
                  />
                </Text>
              )
            }}
          />
        </ImportantNowSpotlight>
      </div>

      {/* EMERGENCY_RESERVE Panel already exists in cashout gem grid */}

      <OverlayPanel
        back={OVERLAY_HELPERS.mortgageRelief.getOrigin()}
        path={OVERLAY_HELPERS.mortgageRelief.buildPath()}
        title={OVERLAY_HELPERS.getTitle('mortgageRelief', intlFormat)}
        anchorOffset={2000}
        component={MortgageRelief}
      />

      <OverlayPanel
        className={styles.valueOverlay}
        back={OVERLAY_HELPERS.shareHomebot.getOrigin()}
        path={OVERLAY_HELPERS.shareHomebot.buildPath()}
        title={OVERLAY_HELPERS.getTitle('shareHomebot', intlFormat)}
        anchorOffset={2000}
        component={SharePanel}
      />
    </>
  )
}

ImportantNow.propTypes = {
  locked: PropTypes.bool
}

export default ImportantNow
